import { Layers, Waves, Route, Link2, Anchor, Plane, CloudRain, Satellite, Radar, Gauge, Siren } from "lucide-react";
import { useT } from "../lib/i18n";

const LAYER_DEFS = [
  { key: "gpm", labelKey: "layer_gpm", testid: "toggle-gpm-layer", icon: CloudRain, color: "#60A5FA" },
  { key: "radar", labelKey: "layer_radar", testid: "toggle-radar-layer", icon: Radar, color: "#2DD4BF" },
  { key: "sentinel", labelKey: "layer_sentinel", testid: "toggle-sentinel-layer", icon: Satellite, color: "#22D3EE" },
  { key: "saih", labelKey: "layer_saih", testid: "toggle-saih-layer", icon: Gauge, color: "#0EA5E9" },
  { key: "ems", labelKey: "layer_ems", testid: "toggle-ems-layer", icon: Siren, color: "#FB923C" },
  { key: "flood", labelKey: "layer_flood", testid: "toggle-flood-layer", icon: Waves, color: "#EF4444" },
  { key: "roads", labelKey: "layer_roads", testid: "toggle-roads-layer", icon: Route, color: "#38BDF8" },
  { key: "bridges", labelKey: "layer_bridges", testid: "toggle-bridges-layer", icon: Link2, color: "#F59E0B" },
  { key: "ports", labelKey: "layer_ports", testid: "toggle-ports-layer", icon: Anchor, color: "#A855F7" },
  { key: "airstrips", labelKey: "layer_airstrips", testid: "toggle-airstrips-layer", icon: Plane, color: "#10B981" },
];

export const LayerToggleHud = ({ visibility, toggle, counts, sources, radarStatus, floodNotice }) => {
  const t = useT();
  const fmtTime = (iso) => {
    try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
  };
  return (
    <div
      className="absolute top-3 left-3 z-[1000] w-80 bg-[#0B0F19]/90 backdrop-blur-md border border-slate-800 rounded-lg p-3 shadow-2xl"
      data-testid="layer-toggle-panel"
    >
      <div className="flex items-center gap-2 mb-2.5 pb-2 border-b border-slate-800">
        <Layers className="h-3.5 w-3.5 text-cyan-400" />
        <span className="text-[11px] font-mono font-semibold tracking-wider uppercase text-slate-300">
          {t.layers_title}
        </span>
      </div>
      <div className="space-y-1.5">
        {LAYER_DEFS.map(({ key, labelKey, testid, icon: Icon, color }) => {
          const on = visibility[key];
          const isRadar = key === "radar";
          const src = isRadar
            ? (radarStatus ? (radarStatus.live ? "live" : "down") : null)
            : sources?.[key];
          const badge = { live: t.live, down: t.radar_off, reference: t.flood_ref, fallback: t.sample };
          const rowTitle = key === "flood" && src === "reference" ? (floodNotice || t.flood_ref_tip) : undefined;
          return (
            <div key={key}>
            <button
              onClick={() => toggle(key)}
              data-testid={testid}
              title={rowTitle}
              className={`w-full flex items-center gap-2 rounded px-2 py-1.5 text-left transition-colors duration-150 ${
                on ? "bg-slate-800/60" : "hover:bg-slate-800/30"
              }`}
            >
              <span
                className="h-2.5 w-2.5 rounded-sm shrink-0 border"
                style={{ backgroundColor: on ? color : "transparent", borderColor: color }}
              />
              <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: on ? color : "#64748B" }} />
              <span className={`flex-1 text-[11px] font-mono truncate ${on ? "text-slate-200" : "text-slate-500"}`}>
                {t[labelKey]}
              </span>
              <span className="text-[10px] font-mono text-slate-500 tabular-nums">{counts?.[key] ?? "—"}</span>
              {src && (
                <span
                  className={`text-[8px] font-mono px-1 rounded ${
                    src === "live"
                      ? "text-emerald-400 bg-emerald-500/10"
                      : src === "down"
                      ? "text-red-400 bg-red-500/10"
                      : "text-amber-400 bg-amber-500/10"
                  }`}
                >
                  {badge[src] || t.sample}
                </span>
              )}
            </button>
            {isRadar && radarStatus && (
              <div
                data-testid="radar-status-line"
                className={`pl-7 pr-2 pb-0.5 text-[9px] font-mono ${radarStatus.live ? "text-emerald-400/80" : "text-red-400/80"}`}
              >
                {radarStatus.live
                  ? `${radarStatus.source || "Radar"} · ${t.radar_updated} ${fmtTime(radarStatus.composite_time || radarStatus.checked_at)}`
                  : t.radar_status_down}
              </div>
            )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
